import * as faceDetection from '@tensorflow-models/face-detection';
export const LOOP_COLOR_S11_LIST = [
  '#007AFF',
  '#AF52DE',
  '#34C759',
  '#FF2D55',
  '#FFCC00',
  '#5AC8FA',
  '#FF9500',
  '#1958D3',
  '#2A3049',
  '#6D00A8',
  '#B48F64',
] as const;

export interface VerificationOption {
  label: string;
  defaultIconName: string;
  isRequire: boolean;
}

// Common contract data structure shared between pre_approve and s11_contract
export interface ContractData {
  s11_document_number: string;
  hire_purchase_header: {
    request_date: string;
    request_no: string;
    chkcontract_name: string;
    chkcontract_eid: string;
  };
  asset_detail: {
    asset_type: string;
    asset_brand: string;
    asset_model: string;
    asset_detail: string;
    asset_vehicle_iden_number: string;
    asset_chassis_number: string;
    asset_status: string;
    asset_use_mileage: string;
    asset_license_plate: string;
  };
  buyer_detail: Record<string, any>;
  price_detail: Record<string, any>;
  guaranty_header: Record<string, any>;
  guarantor_detail: Record<string, any>;
  verification_qr_code?: string | null;
}

// Generic type that can handle both pre_approve and s11_contract
export interface TransformedData<
  T extends 'pre_approve' | 's11_contract' | 'other_reference',
> {
  id: number;
  admin_memo_type: number;
  allow_loa_change: boolean;
  memo_template: number;
  memo_type: string;
  department: any;
  pre_approve?: T extends 'pre_approve' ? ContractData : never;
  s11_contract?: T extends 's11_contract' ? ContractData : never;
  other_reference?: T extends 'other_reference'
    ? ContractData
    : never;
  loa_group?: any;
  loa_detail?: any;
  status?: string;
}

export type TransformedPreApproveData =
  TransformedData<'pre_approve'>;
export type TransformedS11ContractData =
  TransformedData<'s11_contract'>;
export type TransformedOtherReferenceData =
  TransformedData<'other_reference'>;

export interface SmsOtpResponse {
  ref: string;
  ref_display: string;
  phone_number: string;
}

export interface SmsOtpRequestData {
  non_user?: boolean;
  resend?: boolean;
  person_id?: number;
}

export interface SmsVerifyData {
  otp?: string;
  ref?: string;
}

export type SmsActionData = SmsOtpRequestData | SmsVerifyData;

export interface PreApproveDocument {
  id?: number;
  memo_number?: string;
  subject?: string;
  memo_type?: string;
  status?: string;
  can_manage_password?: boolean;
  can_send_reminder?: boolean;
  in_folder_ids?: number[];
}

export interface RequestEvidences {
  has_request_attachments: boolean;
  pass_request_attachments: boolean;
  selfie_video_required: boolean;
  pass_selfie_video: boolean;
  selfie_video_script: string;
  uploaded_pdf: string;
  loa_current_person: string;
}

export interface LoaDetail {
  count: number;
  name: string;
  members: Member[];
  level: number;
  signature_required: boolean;
  user_type: string;
}

export interface Member {
  is_need_e_kyc?: boolean;
  e_kyc_type?: any;
  ddoc_certified_mode?: boolean;
  ddoc_locked_mode?: boolean;
  ddoc_use_26?: boolean;
  ddoc_use_28?: boolean;
  person?: Person;
  verification: Verification;
  no_person?: boolean;
  sequence?: number;
  is_staff?: boolean;
  ddoc_enable?: boolean;
  person_detail?: Person;
}

export interface Person {
  is_custom: boolean;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  job_position?: string;
  phone_number?: string;
  pass_ndid: PassNdid;
  pass_thaicom: PassThaicom;
  person?: Person;
}

export interface PassNdid {
  ever: boolean;
  in_1_month: boolean;
  in_6_month: boolean;
}

export interface PassThaicom {
  ever: boolean;
  in_1_month: boolean;
  in_6_month: boolean;
}

export interface Verification {
  otp_required?: boolean;
  phone_number?: string;
  selfie_video_required?: boolean;
  selfie_video_script?: string;
  has_request_attachments?: boolean;
  request_attachments?: any[];
  loa_member_type?: string;
  is_from_template?: boolean;
}

export interface OnApproveAction {
  skip_qr_code?: boolean;
  error?: boolean;
  render_task_id?: string;
}

export interface Negotiate {
  reason: string;
  negotiate_amount: number;
}

export interface VerificationAction {
  action_type: string;
  member_name: string;
  member_id: number | null;
  person_id: number;
}

export interface TFJSFace {
  box: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
    width: number;
    height: number;
  };
  keypoints?: faceDetection.Keypoint[];
  score?: number;
}

export interface FaceDirection {
  id: number;
  image_detection_types: string[];
  is_limit: boolean;
}

export interface SendFaceDetection {
  id: number;
  transactions: Transaction[];
}

export interface Transaction {
  face_type: string;
  device_type: string;
  image: string | null;
}

export enum S11MemoType {
  preApprove = 'pre_approve',
  s11Contract = 's11_contract',
  otherReference = 'other_reference',
}
export interface DeviceInfo {
  label: string;
  deviceID: string;
}
